import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import {Tab, TabList, TabPanel, Tabs} from 'react-tabs';
import {useHistory, useLocation, useParams} from 'react-router';
import {EventStatus, MatchStatus, MatchUrlParams, Sport, Team, TeamInfo} from 'utils/types';
import {ReactTitle} from 'react-meta-tags';
import {format} from 'date-fns';
import {DFPSlotsProvider , DFPManager} from 'react-dfp';
import {useScrollPosition} from '@n8tb1t/use-scroll-position';
import GA from 'react-ga';
import GA4 from 'react-ga4';
import breakpoints from 'utils/breakpoints';
import {sportNames} from 'utils/sportsUtils';

// components
import PillTabList from 'components/Common/PillTabList';
import Menu from 'components/Common/Menu';
import HeaderInfo from 'components/Match/HeaderInfo';
import WidgetField from 'components/Match/WidgetField';
import Timeline from 'components/Match/Timeline';
import TimelineCommentary from 'components/Match/TimelineCommentary';
import Videos from 'components/Match/Videos';
import WidgetPreview from 'components/Match/WidgetPreview';
import PanelLineupsStats from 'components/Match/PanelLineupsStats';
import PanelScoresTables from 'components/Match/PanelScoresTables';
import AsyncWidgetScores from 'components/Async/AsyncWidgetScores';
import AsyncWidgetStats from 'components/Async/AsyncWidgetStats';
import WidgetBet from 'components/Match/WidgetBet';
import AsyncWidgetLineups from 'components/Async/AsyncWidgetLineups';
import EceArticleView from 'components/Match/EceArticleView';
import AsyncWidgetTables from 'components/Async/AsyncWidgetTables';
import Loader from 'components/Common/Loader';
import Ad from 'components/Common/Ad';
import HeaderMini from 'components/Match/HeaderMini';

// hooks
import useMatchInfo from 'hooks/useMatchInfo';
import useMatchLiveData from 'hooks/useMatchLiveData';
import useTimelineData from 'hooks/useTimelineData';
import usePolling from 'hooks/usePolling';
import useMatchScorers from 'hooks/useMatchScorers';
import useTimelineCommentary from 'hooks/useTimelineCommentary';
import useTimelineCommentaryEnabled from 'hooks/useTimelineCommentaryEnabled';
import useBetData from 'hooks/useBetData';
import useTimelineVideos from 'hooks/useTimelineVideos';
import useBreakpointXL from 'hooks/useBreakpointXL';
import useBreakpointLG from 'hooks/useBreakpointLG';
import useBreakpointMD from 'hooks/useBreakpointMD';

import useTeaserArticles from 'hooks/useTeaserArticles';
import useTVProgramEvents from 'hooks/useTVProgramEvents';
import WidgetTeaser from 'components/Matchcenter/WidgetTeaser';
import WidgetTVProgram from 'components/Matchcenter/WidgetTVProgram';
import WidgetSrLineups from 'components/Match/WidgetSrLineups';
import WidgetSrStats from 'components/Match/WidgetSrStats';
import WidgetSrStandings from 'components/Match/WidgetSrStandings';


import getConfig from '../../getConfig';

import './styles.scss';




// TODO: fallback if URL invalid
// TODO: skeleton loader
const {pollingRates} = getConfig();

const ppid = localStorage.getItem('__PPID') || null;

const getPageTitle = (teams: { home: TeamInfo; away: TeamInfo }, date: Date) =>
    `${teams.home.name} - ${teams.away.name} Live - ${format(date, 'dd-MM-yyyy')} | SPORT24`;

/**
 * Displays the detailed information of a single match (including live data).
 * Handles the business logic of fetching (and polling) the data when the 'matchId' changes.
 * Dispatches a clean-up action on unmount that causes all the detail-related states to be
 * reset to their initial values.
 *
 * In order to improve the overall performances of the page, the business logic of fetching/polling
 * the sidebar tabs data has been split into smaller components (Async widget components) that are
 * connected themselves to the redux store via custom hooks.
 */
const MatchPage: FC = () => {
    const {matchId} = useParams<MatchUrlParams>(); // the current match id
    const location = useLocation();
    const history = useHistory();

    // 24M HOOKS
    const [betDataState, {getBetDataById, cleanup: betCleanup}] = useBetData();
    const [scorers, {getScorersFromTimeline}] = useMatchScorers();
    const [videos, {getVideosFromTimeline}] = useTimelineVideos();
    const commentary = useTimelineCommentary(matchId);
    const [commentaryEnabled, setCommentaryEnabled] = useTimelineCommentaryEnabled();
    const isBreakpointMD = useBreakpointMD();
    const isBreakpointLG = useBreakpointLG();
    const isBreakpointXL = useBreakpointXL();
    const [selectedTab, setSelectedTab] = useState(isBreakpointXL ? 0 : 1);
    const [selectedSideTab, setSelectedSideTab] = useState(0);
    const headerRef = useRef<HTMLElement>(null);
    const [showStuckHeader, setShowStuckHeader] = useState(false);
    const premiumAdRoot = useRef<HTMLElement | null>(null);

    // BI HOOKS
    const {withPolling, clearAllPollings} = usePolling();
    const [matchInfoState, {getMatchInfoById, cleanup}] = useMatchInfo();
    const [matchLiveDataState, {getMatchLiveDataById}] = useMatchLiveData();
    const [timelineState, {getTimelineCmdByEventAndId}] = useTimelineData();

    // the following line returns a polling version of getTimelineCmdByEventAndId function
    const repeatGetTimelineCmdByEventAndId = withPolling(
        getTimelineCmdByEventAndId,
        pollingRates.timeline,
        true
    );
    const repeatGetMatchLiveDataById = withPolling(getMatchLiveDataById, pollingRates.timeline, true);
    const repeatGetBetDataById = withPolling(getBetDataById, pollingRates.betData, true);

    const matchInfo = matchInfoState.data;
    const betDataId = matchInfo?.betDataId;
    const liveData = matchLiveDataState.data;
    const {lastCmdId, updates} = timelineState.data;
    const betData = betDataState.data;

    const [teaserArticlesState, {getTeaserArticles}] = useTeaserArticles();
    const {loading: teaserArticleLoading, data: teaserArticles} = teaserArticlesState;

    const [tvProgramEventsState, {getTVProgramEvents}] = useTVProgramEvents();
    const {loading: tvProgramEventsLoading, data: tvProgramEvents} = tvProgramEventsState;

        /**
     * Article feeds should only be fetched once per component mount. No cleanup necessary.
     */
    useEffect(() => {
        const now = new Date();
        if (matchInfo?.srId === "customEvent") {
            getTVProgramEvents(now);
            getTeaserArticles();
        }
    }, [matchInfo]);

    const isApp = useMemo(() => {
        const query = new URLSearchParams(location.search);
        const appParam = query.get('sport24app');

        return appParam === 'true';
    }, [location]);

    /**
     * Show Stuck Scoreboard on Scroll
     */
    useScrollPosition(({currPos}) => {
        const menuHeight = 60;
        const headerHeight = headerRef.current?.getBoundingClientRect().height ?? 0;

        if (currPos.y < -1 * headerHeight) setShowStuckHeader(true);
        else setShowStuckHeader(false);
    });

    /**
     * When the matchId changes, fetches the match basic information and relative live data.
     */
    useEffect(() => {
        getMatchInfoById(matchId);
        repeatGetMatchLiveDataById(matchId);

        // cleans up the matchDetail state when the component un-mounts
        return () => {
            cleanup();
            repeatGetMatchLiveDataById.cancel();
        };
    }, [matchId, getMatchInfoById, repeatGetMatchLiveDataById, cleanup]);

    useEffect(() => {
        if (betDataId) repeatGetBetDataById(betDataId);

        return () => {
            betCleanup();
            repeatGetBetDataById.cancel();
        };
    }, [betCleanup, betDataId, repeatGetBetDataById]);

    /**
     * When the matchId changes or the lastCmdId changes, repeat fetching the commands
     */
    useEffect(() => {
        repeatGetTimelineCmdByEventAndId(matchId, lastCmdId);

        return repeatGetTimelineCmdByEventAndId.cancel;
    }, [matchId, lastCmdId, repeatGetTimelineCmdByEventAndId]);

    /**
     * When the timeline changes, calculate other visualizations of its data
     */
    useEffect(() => {
        getScorersFromTimeline(timelineState);
        getVideosFromTimeline(timelineState);
    }, [timelineState, getScorersFromTimeline, getVideosFromTimeline]);

    /**
     * Calculate main & side tabs based on content & viewport
     */
    const tabs = useMemo(
        () => ({
            main: {
                ...(matchInfo?.sport !== Sport.SOCCER ? {'#livematch': 'LIVE MATCH'} : {'#livematch':<>LIVE <br />MATCH</>}),
                ...(matchInfo?.sport === Sport.SOCCER ? {
                ...(!isBreakpointLG && matchInfo?.srId!=="customEvent" 
                    ? {
                        '#lineups': 'LINEUPS',
                        '#stats': <>LIVE <br />STATS</>,
                        '#preview': <>PREVIEW & <br /> LIVE STANDINGS</>
                        } : {}),
                    }: {
                    ...(!isBreakpointLG && matchInfo?.srId!=="customEvent" 
                    ? {
                        '#preview': 'PREVIEW',
                        ...(matchInfo?.sport !== Sport.VOLLEYBALL
                            ? {
                                '#stats': 'STATS'
                            }
                            : {}),
                        // '#scores': <>SCORES <br />& TABLES</>,
                    }
                    : {}),
                    }),

                ...(videos && videos.length ? {'#videos': 'VIDEOS'} : {}),
            },
            // only show side tabs on 1024+ viewports - no hash indexes because side tabs don't use hash urls
            side: isBreakpointLG
                ? [
                    'PREVIEW',
                    ...(matchInfo?.sport === Sport.SOCCER ? ['LINEUPS'] : []),
                    ...(matchInfo?.sport !== Sport.VOLLEYBALL ? ['LIVE STATS'] : []),
                    ...(matchInfo?.sport === Sport.SOCCER ? ['LIVE STANDINGS'] : []),

                    // 'SCORES',
                    // 'TABLES',
                ]
                : [],
        }),
        [isBreakpointLG, matchInfo?.sport, matchInfo?.srId, videos]
    );

    /**
     * When resizing (e.g. portrait / landscape) and tab layouts change, keep live match tab visible.
     */
    useEffect(() => {
        if (isBreakpointLG) {
            setSelectedTab(0);
            setSelectedSideTab(0);
        } else {
            setSelectedTab(0);
        }
    }, [isBreakpointLG]);

    /**
     * When hash in URL matches a main tab, switch to it.
     */
    useEffect(() => {
        const index = Object.keys(tabs.main).findIndex(tabHash => tabHash === location.hash);
        setSelectedTab(index > -1 ? index : 0);
    }, [location, tabs.main]);

    // get billboard ad root
    useEffect(() => {
        if (!premiumAdRoot.current){
            premiumAdRoot.current = document.getElementById('matchcenter-premium-root');
            premiumAdRoot.current?.classList.remove("adSlot-height--premium"); 
        }
            
    }, []);

    /**
     * Google Analytics
     */
    useEffect(() => {
        // fire pageview only if document source already has match title, or (if source has main page title) after matchInfo is fetched
        if (!document.title.includes('Matchcenter') || matchInfo) {
            GA.initialize('UA-482127-1');
            GA.ga('send', 'pageview', {
                page: `${process.env.PUBLIC_URL}${location.pathname}${location.search}`,
                title: matchInfo ? getPageTitle(matchInfo.teams, matchInfo.date) : document.title,
            });
        }
        // console.log(' Google Analytics UA');
    }, [location]); // don't include matchInfo as dependency to prevent duplicate pageviews when matchInfo is fetched asynchronously.

    /**
     * Google Analytics 4
     */
    useEffect(() => {
        // fire pageview only if document source already has match title, or (if source has main page title) after matchInfo is fetched
        if (!document.title.includes('Matchcenter') || matchInfo) {
            GA4.initialize([
                {
                    trackingId: 'G-CBFPZXDS6N',
                },
    
                {
                    trackingId: 'G-NTXG7V0NM2',
                },
            ]);
            GA4.ga('send', 'pageview', {
                page: `${process.env.PUBLIC_URL}${location.pathname}${location.search}`,
                title: matchInfo ? getPageTitle(matchInfo.teams, matchInfo.date) : document.title,
            });
        }
        // console.log(' Google Analytics 4');
    }, [location]); // don't include matchInfo as dependency to prevent duplicate pageviews when matchInfo is fetched asynchronously.


    /**
     * Pushing PPID in every add request
     */
    useMemo(() => {
        if (ppid) {
            DFPManager.getGoogletag().then((googletag) => {
                googletag.cmd.push(() => {
                  googletag.pubads().setPublisherProvidedId(ppid);
                });
            });
        }
    }, []);

    /**
     * The when component un-mounts, clear all the polling functions
     */
    useEffect(() => clearAllPollings, [clearAllPollings]);

    // FIXME: the Loading component should be bound to the `loading` flags,
    // then if no matchInfo is found it just means the provided id is invalid -> the page is a 404
    if (!(matchInfo && liveData)) return <Loader fullpage/>;

    const {
        srId,
        date,
        sport,
        competition,
        competitionSlug,
        seasonId,
        lmtSponsor,
        teams,
        teamRankings,
        probabilities,
        formaMatches,
        formaHome,
        formaAway,
        eceArticleContent,
        providerView
    } = matchInfo;

    const {scores, matchStatus, minute, stoppageTime, status, periodScores} = liveData;
    const eceSport = sportNames.find(s => s.sport === sport)?.eceSlug;

    return (
        <DFPSlotsProvider
            dfpNetworkId="4834629"
            targetingArguments={{
                path: window.location.href,
                isMobile: !isBreakpointMD,
                type: 'art',
                article: matchId,
                category: [...(eceSport ? [eceSport] : [])],
                keywords: [
                    ...(teams.home.slug ? [teams.home.slug] : []),
                    ...(teams.away.slug ? [teams.away.slug] : []),
                    competitionSlug,
                ],
            }}
            lazyLoad={{fetchMarginPercent: 500, renderMarginPercent: 10, mobileScaling: 2.0}}
        >
           <Ad
                root={premiumAdRoot.current}
                adUnit="sport24.gr/Matchcenter_ros_Billboard"
                scope="matchcenterpage"
                sizes={[
                    [970, 250],
                    [970, 90],
                    [728, 90],
                ]}
                sizeMapping={[
                    {
                        viewport: [breakpoints.xl, 0],
                        sizes: [
                            [970, 250],
                            [970, 90],
                        ],
                    },
                    {
                        viewport: [0, 0],
                        sizes: [],
                    }
                ]}
            /> 
            <div className="MatchPage">
                <ReactTitle title={getPageTitle(teams, date)}/>
                <HeaderMini
                    date={matchInfo.date}
                    minute={minute}
                    stoppageTime={stoppageTime}
                    sport={sport}
                    teams={teams}
                    status={status}
                    matchStatus={matchStatus}
                    scores={scores}
                    visible={showStuckHeader}
                    isApp={isApp}
                />
                <header className="MatchPage__header">
                    <HeaderInfo
                        matchInfo={matchInfo}
                        scorers={scorers}
                        liveData={liveData}
                        ref={headerRef}
                        // betLink={betData?.externalLink}
                        betData={betData}
                    />
                </header>
                {(matchInfo?.seasonCompetitionId === "sr:competition:185" || matchInfo?.seasonCompetitionId === "sr:competition:138") && (
                    <Ad
                        adUnit="sport24.gr/Matchcenter_1440x100"
                        scope="matchpage"
                        sizes={[
                            [1440, 100],
                        ]}
                        sizeMapping={[
                            {
                                    viewport: [breakpoints.xxl, 0],
                                    sizes: [
                                        [1440, 100],
                                    ],
                            },
                            {
                                viewport: [0, 0],
                                sizes: [],
                            }
                        ]}
                    /> 
                )}
                <main
                    className={
                        `MatchPage__main ${
                            isBreakpointLG && !(videos && videos.length) ? 'MatchPage__main--no-tabs' : ''
                        }` /* FIXME better tabs visibility check */
                    }
                >
                    {isBreakpointXL && (
                        <section className="MatchPage__left">
                            {betData?.odds && betData.odds.length > 0 && (providerView === "All" || providerView === "Matchcenter") && (status === EventStatus.NOT_STARTED || status === EventStatus.LIVE) && (
                                <WidgetBet betData={betData}/>
                            )}
                            {matchInfo.srId === "customEvent" && (
                                <WidgetTeaser loading={teaserArticleLoading} articles={teaserArticles}/>

                            )}
                            <Ad
                                adUnit="sport24.gr/Matchcenter_ros_300x250A"
                                scope="matchpage"
                                sizes={[
                                    [300, 600],
                                    [300, 250],
                                ]}
                                sizeMapping={[
                                    {
                                        viewport: [breakpoints.lg, 0],
                                        sizes: [
                                            [300, 600],
                                            [300, 250],
                                        ],
                                    },
                                    {viewport: [0, 0], sizes: []},
                                ]}
                                sticky
                                /* [TW-41160742] remove ads refresh
                                    autoRefresh
                                */
                            />
                        </section>
                    )}
                    <section className="MatchPage__center">
                        <Tabs
                            selectedIndex={selectedTab}
                            onSelect={(index, prevIndex) => {
                                if (index !== prevIndex) {
                                    setSelectedTab(index);
                                    history.push(Object.keys(tabs.main)[index]);
                                }
                            }}
                        >
                            <TabList
                                className={
                                    `MatchPage__tabs ${
                                        isBreakpointLG && !(videos && videos.length) ? 'MatchPage__tabs--hidden' : ''
                                    }` /* FIXME for dynamic check */
                                }
                            >
                                {Object.entries(tabs.main).map(([tabHash, tabLabel]) => (
                                    <Tab
                                        key={tabHash}
                                        className="MatchPage__tab"
                                        selectedClassName="MatchPage__tab--selected"
                                    >
                                        {tabLabel}
                                    </Tab>
                                ))}
                            </TabList>
                            {/*
            eceArticleContent && (
              <TabPanel
                className="MatchPage__panel MatchPage__panel--post-game"
                selectedClassName="MatchPage__panel--selected"
              >
                <EceArticleView article={eceArticleContent} />
              </TabPanel>
            )
            */}
                            <TabPanel
                                className="MatchPage__panel MatchPage__panel--live"
                                selectedClassName="MatchPage__panel--selected"
                                forceRender
                            >
                                {!isBreakpointLG &&
                                <WidgetField srId={srId} seasonId={seasonId} lmtSponsor={lmtSponsor} sport={sport} />}

                                {!isBreakpointLG && (
                                    <TimelineCommentary
                                        commentary={commentary}
                                        commentaryEnabled={commentaryEnabled}
                                        setCommentaryEnabled={setCommentaryEnabled}
                                    />
                                )}
                                <Timeline
                                    updates={updates}
                                    sport={sport}
                                    teams={teams}
                                    commentaryEnabled={commentaryEnabled}
                                />
                            </TabPanel>

                            {
                                /* MOBILE - PREVIEW */
                                !isBreakpointLG && (
                                    <>
                                    {
                                        matchInfo.srId!=="customEvent" && (
                                            <TabPanel
                                                className="MatchPage__panel MatchPage__panel--preview"
                                                selectedClassName="MatchPage__panel--selected"
                                            >

                                            {
                                                sport === Sport.SOCCER ? (                                           
                                                    <WidgetSrLineups 
                                                        srId={srId} 
                                                        seasonId={seasonId} 
                                                        lmtSponsor={lmtSponsor}
                                                    />
                                                )
                                                : 
                                                (
                                                    <WidgetPreview
                                                        sport={sport}
                                                        teams={teams}
                                                        teamRankings={teamRankings}
                                                        probabilities={probabilities}
                                                        formaMatches={formaMatches}
                                                        formaHome={formaHome}
                                                        formaAway={formaAway}
                                                    />   
                                                )


                                            }

                                        </TabPanel>
                                        )
                                    }
                                    </>


                                )
                            }
                            {
                                /* MOBILE - LIVE STATS */
                                !isBreakpointLG && matchInfo.srId!=="customEvent" &&(
                                    <TabPanel
                                        className="MatchPage__panel MatchPage__panel--lineups-stats"
                                        selectedClassName="MatchPage__panel--selected"
                                    >

                                        {
                                            sport === Sport.SOCCER ? (
                                                <WidgetSrStats 
                                                    srId={srId} 
                                                    seasonId={seasonId} 
                                                    lmtSponsor={lmtSponsor}
                                                />
                                            ) : (
                                                <PanelLineupsStats
                                                    sport={sport}
                                                    teams={teams}
                                                    matchId={matchId}
                                                    updates={updates}
                                                    srId={srId}
                                                    seasonId={seasonId}
                                                    lmtSponsor={lmtSponsor}
                                                    teamRankings={teamRankings}
                                                    probabilities={probabilities}
                                                    formaMatches={formaMatches}
                                                    formaHome={formaHome}
                                                    formaAway={formaAway}
                                                />  
                                            )
                                        }

                                        {!isBreakpointMD && (
                                            <Ad
                                                adUnit="sport24.gr/Matchcenter_ros_300x250A"
                                                scope="matchpage"
                                                sizes={[
                                                    [300, 600],
                                                    [300, 250],
                                                    [320, 100],
                                                ]}
                                                sizeMapping={[
                                                    {
                                                        viewport: [breakpoints.md, 0],
                                                        sizes: [],
                                                    },
                                                    {
                                                        viewport: [0, 0],
                                                        sizes: [
                                                            [300, 600],
                                                            [300, 250],
                                                            [320, 100],
                                                        ],
                                                    },
                                                ]}
                                            />
                                        )}
                                    </TabPanel>
                                )
                            }
                            {/* MOBILE - SCORES & TABLES  */}
                                
{                                !isBreakpointLG && (
                                  <TabPanel
                                    className="MatchPage__panel MatchPage__panel--scores-tables"
                                    selectedClassName="MatchPage__panel--selected"
                                  >
                                    <PanelLineupsStats
                                        sport={sport}
                                        teams={teams}
                                        matchId={matchId}
                                        updates={updates}
                                        srId={srId}
                                        seasonId={seasonId}
                                        lmtSponsor={lmtSponsor}
                                        teamRankings={teamRankings}
                                        probabilities={probabilities}
                                        formaMatches={formaMatches}
                                        formaHome={formaHome}
                                        formaAway={formaAway}
                                    />
                                    {/* <PanelScoresTables
                                      seasonId={matchInfo?.seasonId}
                                      groupId={matchInfo?.groupId}
                                      competition={competition}
                                    /> */}
                                     {/* TODO add ros billboard for mobile */}
                                  </TabPanel>
                                )
                               }
                            {
                                /* VIDEOS */
                                videos && !!videos.length && (
                                    <TabPanel
                                        className="MatchPage__panel MatchPage__panel--videos"
                                        selectedClassName="MatchPage__panel--selected"
                                        forceRender
                                    >
                                        <Videos videos={videos}/>
                                    </TabPanel>
                                )
                            }
                        </Tabs>
                    </section>
                    {
                        /* DESKTOP - SIDEBAR TABS */
                        isBreakpointLG && (
                            <section className="MatchPage__right">
                                {matchInfo.srId==="customEvent" && (
                                    <WidgetTVProgram loading={tvProgramEventsLoading} tvData={tvProgramEvents}/>

                                )}
                                {isBreakpointLG && <WidgetField srId={srId} seasonId={seasonId} lmtSponsor={lmtSponsor} sport={sport} />}
                                {isBreakpointLG && (
                                    <TimelineCommentary
                                        commentary={commentary}
                                        commentaryEnabled={commentaryEnabled}
                                        setCommentaryEnabled={setCommentaryEnabled}
                                    />
                                )}
                                {matchInfo.srId!=="customEvent" && (
                                    <Tabs
                                        className="SideTabs"
                                        selectedIndex={selectedSideTab}
                                        onSelect={(index, prevIndex) => index !== prevIndex && setSelectedSideTab(index)}
                                    >
                                        {
                                            PillTabList({
                                                tabs: tabs.side,
                                            }) /* component used as a function due to issue https://github.com/reactjs/react-tabs/issues/208 */
                                        }
                                        <TabPanel>
                                            <WidgetPreview
                                                sport={sport}
                                                teams={teams}
                                                teamRankings={teamRankings}
                                                probabilities={probabilities}
                                                formaMatches={formaMatches}
                                                formaHome={formaHome}
                                                formaAway={formaAway}
                                            />
                                        </TabPanel>
                                        {sport === Sport.SOCCER && (
                                            <TabPanel>
                                                {/* <AsyncWidgetLineups
                                                    matchId={matchId}
                                                    updates={updates}
                                                    team={Team.UNDEFINED}
                                                /> */}
                                                {isBreakpointLG && <WidgetSrLineups srId={srId} seasonId={seasonId} lmtSponsor={lmtSponsor}/>}
                                            </TabPanel>
                                        )}
                                        {(sport !== Sport.VOLLEYBALL && sport !== Sport.SOCCER) && (
                                            <TabPanel>
                                                <AsyncWidgetStats matchId={matchId} teams={teams}/>
                                            </TabPanel>
                                        )}
                                        {sport === Sport.SOCCER && (
                                            <>
                                                <TabPanel>
                                                    {isBreakpointLG && <WidgetSrStats srId={srId} seasonId={seasonId} lmtSponsor={lmtSponsor}/>}
                                                </TabPanel>
                                                <TabPanel>
                                                    {isBreakpointLG && <WidgetSrStandings srId={srId} seasonId={seasonId} lmtSponsor={lmtSponsor}/>}
                                                </TabPanel>
                                            </>
                                        )}

                                    {/*
                  <TabPanel>
                    <AsyncWidgetScores
                      seasonId={matchInfo?.seasonId}
                    />
                  </TabPanel>
                  <TabPanel>
                    <AsyncWidgetTables
                      seasonId={matchInfo?.seasonId}
                      groupId={matchInfo?.groupId}
                      competition={competition}
                    />
                  </TabPanel>
                */}
                                    </Tabs>
                                )}
                                <Ad
                                    adUnit="sport24.gr/Matchcenter_ros_300x250B"
                                    scope="matchpage"
                                    sizes={[
                                        [300, 600],
                                        [300, 250],
                                    ]}
                                    sizeMapping={[
                                        {
                                            viewport: [breakpoints.lg, 0],
                                            sizes: [
                                                [300, 600],
                                                [300, 250],
                                            ],
                                        },
                                        {viewport: [0, 0], sizes: []},
                                    ]}
                                    sticky
                                    /* [TW-41160742] remove ads refresh
                                        autoRefresh
                                    */
                                />
                            </section>
                        )
                    }
                </main>
                {!isBreakpointLG && (
                    // FIXME: umnount his and destroy slot gracefully OR mount and setup
                    <Ad
                        adUnit="sport24.gr/Matchcenter_ros_300x50stuck"
                        scope="matchpage"
                        sizes={[[320, 50]]}
                        stuck
                        /* [TW-41160742] remove ads refresh
                            autoRefresh
                        */
                    />
                )}
            </div>
        </DFPSlotsProvider>
    );
};

export default MatchPage;
